/**
 * BespokeBridge – Events Management Module API
 * Covers all 5 tools: Event Setup & Configuration, Registration & Waitlist,
 * Attendance & Check-In, Event Calendar & Discovery, Event Content & Media
 *
 * All event-related Firestore collections are stored at the ROOT level (not as
 * subcollections under organizations/{orgId}). Each document carries an `orgId`
 * field so queries can be scoped to the correct organisation.
 *
 * Root collections used here:
 *   events              – orgId, status, start_datetime, …
 *   registrations       – orgId, event_id, member_id, registrant_type, …
 *   media               – orgId, event_id, type, moderation_status, …
 *   volunteer_signups   – orgId, event_id, volunteer_id, …
 *
 * Collections left in organizations/{orgId}/ subcollections (not migrated):
 *   memberEventHistory  – small, per-org doc per member
 */
import {
  collection, doc, getDoc, getDocs, addDoc, setDoc,
  updateDoc, deleteDoc, query, where, orderBy, serverTimestamp,
  runTransaction, writeBatch, limit, Timestamp, increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../utils/firebase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Root-level collection helpers
const eventsCol   = ()   => collection(db, 'events');
const eventDoc    = (id) => doc(db, 'events', id);
const regsCol     = ()   => collection(db, 'registrations');
const regDoc      = (id) => doc(db, 'registrations', id);
const mediaCol    = ()   => collection(db, 'media');
const mediaDocRef = (id) => doc(db, 'media', id);
const signupsCol  = ()   => collection(db, 'volunteer_signups');
const signupDocRef= (id) => doc(db, 'volunteer_signups', id);

// Org-scoped helpers kept for non-migrated subcollections (memberEventHistory)
const orgCol  = (orgId, col)     => collection(db, 'organizations', orgId, col);
const orgDoc  = (orgId, col, id) => doc(db, 'organizations', orgId, col, id);

const now   = () => serverTimestamp();
// Always use the Firestore document ID — strip any 'id' field baked into the
// document data (e.g. events created from proposals carry the proposal's id).
const toArr = snap => snap.docs.map(d => {
  const { id: _ignored, ...data } = d.data();
  return { id: d.id, ...data };
});

/** Generate a unique QR token for a registration */
export const generateQRToken = (orgId, eventId, memberId) =>
  `${orgId}-${eventId}-${memberId}-${Date.now().toString(36)}`;

/** QR code image URL (uses free qrserver.com API) */
export const getQRCodeUrl = (token, size = 200) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(token)}&margin=10`;

/** Format Firestore Timestamp or ISO string to display date */
export const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-SG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Days until a Firestore Timestamp */
export const daysUntil = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 1 — EVENT SETUP & CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

export const EVENT_CATEGORIES = [
  { value: 'CIRCLE_TIME',         label: 'Circle Time' },
  { value: 'TALK_WORKSHOP',       label: 'Talk & Workshop' },
  { value: 'SOCIAL_RECREATIONAL', label: 'Social & Recreational' },
  { value: 'OTHER',               label: 'Other' },
];

export const EVENT_STATUSES = ['DRAFT', 'PUBLISHED', 'CANCELLED', 'COMPLETED', 'ARCHIVED'];

/** Normalise a datetime value (string, JS Date, or Firestore Timestamp) to a
 *  Firestore Timestamp so every event document stores the same type.
 *  Returns null when the input is falsy. */
const toTimestamp = (v) => {
  if (!v) return null;
  if (v && typeof v.toDate === 'function') return v;           // already a Timestamp
  if (v instanceof Date) return Timestamp.fromDate(v);          // JS Date
  const d = new Date(v);                                        // datetime-local string
  return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
};

/** Sort events by start_datetime ascending (handles both Timestamp and string values) */
const sortByStart = (events) => {
  return [...events].sort((a, b) => {
    const toMs = ts => {
      if (!ts) return 0;
      if (ts.toDate) return ts.toDate().getTime();
      const d = new Date(ts);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    };
    return toMs(a.start_datetime) - toMs(b.start_datetime);
  });
};

export const getEvents = async (orgId, statusFilter = null) => {
  // Fetch all events for this org and filter/sort client-side.
  // This avoids composite index requirements and works regardless of whether
  // start_datetime is stored as a Firestore Timestamp or a legacy string.
  // Proposals (record_type === 'proposal') are excluded — use eventProposalsApi for those.
  const q = query(eventsCol(), where('orgId', '==', orgId));
  const all = toArr(await getDocs(q));
  const nonProposals = all.filter(e => e.record_type !== 'proposal');
  const filtered = statusFilter
    ? nonProposals.filter(e => e.status === statusFilter)
    : nonProposals;
  return sortByStart(filtered);
};

export const getPublishedEvents = async (orgId) => {
  const q = query(eventsCol(), where('orgId', '==', orgId), where('status', '==', 'PUBLISHED'));
  return sortByStart(toArr(await getDocs(q)));
};

export const getEvent = async (orgId, eventId) => {
  const d = await getDoc(eventDoc(eventId));
  return d.exists() ? { id: d.id, ...d.data() } : null;
};

export const createEvent = async (orgId, data, createdBy) => {
  const eventData = {
    ...data,
    // Always store datetimes as Firestore Timestamps regardless of input type
    start_datetime: toTimestamp(data.start_datetime),
    end_datetime:   toTimestamp(data.end_datetime),
    orgId,
    status: 'DRAFT',
    created_by: createdBy,
    assigned_volunteers: data.assigned_volunteers || [],
    tags: data.tags || [],
    photo_consent_required: data.photo_consent_required ?? false,
    no_show_threshold: data.no_show_threshold || 3,
    // Dual-capacity model
    member_capacity:            data.member_capacity    != null ? parseInt(data.member_capacity, 10) : null,
    volunteer_capacity:         data.volunteer_capacity != null ? parseInt(data.volunteer_capacity, 10) : null,
    attendee_tracking_enabled:  data.attendee_tracking_enabled ?? false,
    created_at: now(),
    updated_at: now()
  };
  // Add speaker only for Talks
  if (data.category === 'TALK_WORKSHOP') {
    eventData.speaker = data.speaker || { name: '', bio: '', headshot_url: '' };
  }
  const ref = await addDoc(eventsCol(), eventData);
  return ref.id;
};

// ─── Registration form configuration (QR & Comms tab) ─────────────────────────
// Mirrors DEFAULT_FIELDS in backend/utils/eventRegistration.js, which is authoritative for
// validation. `locked` fields can't be disabled or made optional.
export const DEFAULT_REGISTRATION_FIELDS = [
  { key: 'name',             label: 'Name (as per NRIC)',        type: 'text',   enabled: true, required: true,  locked: true },
  { key: 'email',            label: 'Email',                     type: 'email',  enabled: true, required: true,  locked: true },
  { key: 'phone',            label: 'Telephone',                 type: 'tel',    enabled: true, required: true },
  { key: 'gender',           label: 'Gender',                    type: 'select', enabled: true, required: true },
  { key: 'residency_status', label: 'Residency status',          type: 'select', enabled: true, required: true },
  { key: 'nric_last4',       label: 'Last 4 characters of NRIC', type: 'nric4',  enabled: true, required: true },
  { key: 'birth_year',       label: 'Year of birth',             type: 'year',   enabled: true, required: true },
  { key: 'subscriptions',    label: 'Subscription preferences (Email, Push Notifications, WhatsApp)', type: 'subscriptions', enabled: true, required: false },
];

export const CUSTOM_FIELD_TYPES = [
  { value: 'text',     label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select',   label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'number',   label: 'Number' },
  { value: 'date',     label: 'Date' },
];

/** Merge an event's saved registration_form over the defaults (defaults first, then custom fields). */
export const resolveRegistrationFields = (event) => {
  const saved = Array.isArray(event?.registration_form?.fields) ? event.registration_form.fields : [];
  const byKey = new Map(saved.map(f => [f.key, f]));
  const builtins = DEFAULT_REGISTRATION_FIELDS.map(def => {
    const s = byKey.get(def.key);
    if (!s || def.locked) return { ...def };
    return { ...def, enabled: s.enabled !== false, required: def.type === 'subscriptions' ? false : !!s.required };
  });
  const customs = saved.filter(f => typeof f.key === 'string' && f.key.startsWith('custom_'));
  return [...builtins, ...customs];
};

export const saveRegistrationForm = (orgId, eventId, fields) =>
  updateDoc(eventDoc(eventId), { registration_form: { fields }, updated_at: now() });

/** Public self-registration page for an event (no login needed). */
export const getRegistrationUrl = (orgId, eventId) =>
  `${window.location.origin}/BespokeBridge/${orgId}/register/${eventId}`;

// ─── Event-change notifications ──────────────────────────────────────────────
// When any of these change on an upcoming, published event, every CONFIRMED / WAITLISTED
// registrant gets the "Changes to Registered Event" triggered campaign (email + push).
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const msOf = (v) => {
  if (!v) return null;
  const d = v.toDate ? v.toDate() : new Date(v);
  return isNaN(d.getTime()) ? null : d.getTime();
};

/** Compare the stored event with the fields being saved; returns [{ field, from }] for real changes. */
const diffNotifiableFields = (before, data) => {
  const changes = [];
  const iso = (v) => (msOf(v) != null ? new Date(msOf(v)).toISOString() : null);
  if (data.start_datetime !== undefined && msOf(before.start_datetime) !== msOf(toTimestamp(data.start_datetime))) {
    changes.push({ field: 'start_datetime', from: iso(before.start_datetime) });
  }
  if (data.end_datetime !== undefined && msOf(before.end_datetime) !== msOf(toTimestamp(data.end_datetime))) {
    changes.push({ field: 'end_datetime', from: iso(before.end_datetime) });
  }
  if (data.location !== undefined) {
    const norm = (v) => String(v || '').trim();
    if (norm(before.location?.name) !== norm(data.location?.name)) {
      changes.push({ field: 'location_name', from: norm(before.location?.name) });
    }
    if (norm(before.location?.address) !== norm(data.location?.address)) {
      changes.push({ field: 'location_address', from: norm(before.location?.address) });
    }
  }
  return changes;
};

export const notifyEventChanges = async (orgId, eventId, changes) => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Not signed in.');
  const res = await fetch(`${API_URL}/api/org/${orgId}/events/${eventId}/notify-changes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ changes }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) throw new Error(body.message || `Notification request failed (${res.status})`);
  return body;
};

/**
 * Update an event. If a notifiable field (start/end date-time, location name/address) of a
 * PUBLISHED event changed, registrants are notified. The save itself never fails because of
 * notification problems — check `result.notifyError`.
 * @returns {{ notify: object|null, notifyError: string|null }}
 */
export const updateEvent = async (orgId, eventId, data) => {
  const touchesNotifiable = data.start_datetime !== undefined || data.end_datetime !== undefined || data.location !== undefined;
  const before = touchesNotifiable ? await getEvent(orgId, eventId) : null;

  await updateDoc(eventDoc(eventId), {
    ...data,
    // Normalise datetimes on edit as well
    ...(data.start_datetime !== undefined && { start_datetime: toTimestamp(data.start_datetime) }),
    ...(data.end_datetime   !== undefined && { end_datetime:   toTimestamp(data.end_datetime)   }),
    updated_at: now(),
  });

  const result = { notify: null, notifyError: null };
  if (before && before.status === 'PUBLISHED') {
    const changes = diffNotifiableFields(before, data);
    if (changes.length) {
      try {
        result.notify = await notifyEventChanges(orgId, eventId, changes);
      } catch (e) {
        console.error('[updateEvent] change notification failed:', e);
        result.notifyError = e.message || 'Could not notify registrants.';
      }
    }
  }
  return result;
};

export const publishEvent = async (orgId, eventId) => {
  console.log('[publishEvent] path = events/' + eventId);
  // setDoc with merge:true is more robust than updateDoc — it works whether or
  // not the document already exists, and doesn't fail on a "no document" error.
  await setDoc(eventDoc(eventId), {
    status: 'PUBLISHED', published_at: now(), updated_at: now()
  }, { merge: true });
  console.log('[publishEvent] done');
};

export const cancelEvent = async (orgId, eventId, reason, cancelledBy) => {
  await updateDoc(eventDoc(eventId), {
    status: 'CANCELLED', cancellation_reason: reason,
    cancelled_by: cancelledBy, cancelled_at: now(), updated_at: now()
  });
};

export const archiveEvent = async (orgId, eventId) => {
  await updateDoc(eventDoc(eventId), {
    status: 'ARCHIVED', updated_at: now()
  });
};

export const completeEvent = async (orgId, eventId) => {
  await updateDoc(eventDoc(eventId), {
    status: 'COMPLETED', completed_at: now(), updated_at: now()
  });
};

export const cloneEvent = async (orgId, eventId, createdBy) => {
  const original = await getEvent(orgId, eventId);
  if (!original) throw new Error('Event not found');
  const { id, status, created_at, updated_at, published_at, ...rest } = original;
  const cloneRef = await addDoc(eventsCol(), {
    ...rest,
    orgId,
    title: `Copy of ${rest.title}`,
    status: 'DRAFT',
    created_by: createdBy,
    created_at: now(),
    updated_at: now()
  });
  return cloneRef.id;
};

/** Generate recurring event instances from a base event */
export const generateRecurringInstances = async (orgId, baseEventId, numInstances = 12) => {
  const base = await getEvent(orgId, baseEventId);
  if (!base || base.event_type !== 'RECURRING' || !base.recurrence_rule) return [];

  const { frequency, day_of_week, nth_occurrence } = base.recurrence_rule;
  const startDt = base.start_datetime?.toDate ? base.start_datetime.toDate() : new Date(base.start_datetime);
  const endDt   = base.end_datetime?.toDate   ? base.end_datetime.toDate()   : new Date(base.end_datetime);
  const duration = endDt - startDt;

  const instances = [];
  let current = new Date(startDt);

  for (let i = 1; i <= numInstances; i++) {
    if (frequency === 'MONTHLY') {
      current = new Date(current.setMonth(current.getMonth() + 1));
    } else if (frequency === 'WEEKLY') {
      current = new Date(current.setDate(current.getDate() + 7));
    } else break;

    const instanceStart = new Date(current);
    const instanceEnd   = new Date(current.getTime() + duration);

    const { id, recurrence_rule, event_type, ...rest } = base;
    const instanceRef = await addDoc(eventsCol(), {
      ...rest,
      orgId,
      title: `${base.title} (#${i + 1})`,
      event_type: 'ONE_OFF',
      parent_event_id: baseEventId,
      instance_number: i + 1,
      start_datetime: Timestamp.fromDate(instanceStart),
      end_datetime: Timestamp.fromDate(instanceEnd),
      status: 'DRAFT',
      created_at: now(),
      updated_at: now()
    });
    instances.push(instanceRef.id);
  }
  return instances;
};

export const getVolunteerEvents = async (orgId, volunteerId) => {
  const q = query(
    eventsCol(),
    where('orgId', '==', orgId),
    where('assigned_volunteers', 'array-contains', volunteerId)
  );
  const events = toArr(await getDocs(q));
  return events.filter(e => ['PUBLISHED', 'COMPLETED'].includes(e.status));
};

// ── Volunteer sign-ups ────────────────────────────────────────────────────────

/** Get published events that require volunteers — client-side filtered */
export const getVolunteerOpportunities = async (orgId) => {
  const all = toArr(await getDocs(query(eventsCol(), where('orgId', '==', orgId))));
  return all
    .filter(e => e.status === 'PUBLISHED' && e.volunteers_required === true)
    .sort((a, b) => {
      const toMs = ts => ts?.toDate ? ts.toDate().getTime() : new Date(ts || 0).getTime();
      return toMs(a.start_datetime) - toMs(b.start_datetime);
    });
};

/** Get a single volunteer's signup doc for an event (null if not signed up) */
export const getVolunteerSignup = async (orgId, eventId, volunteerId) => {
  if (!volunteerId) return null;
  const q = query(
    signupsCol(),
    where('orgId', '==', orgId),
    where('event_id', '==', eventId),
    where('volunteer_id', '==', volunteerId)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

/** Get all volunteer signups for an event */
export const getVolunteerSignups = async (orgId, eventId) => {
  const snap = await getDocs(
    query(signupsCol(), where('orgId', '==', orgId), where('event_id', '==', eventId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/** Volunteer signs up for an event */
export const signUpAsVolunteer = async (orgId, eventId, volunteerId, volunteerName) => {
  // Check for an existing signup first (outside transaction — queries can't run in transactions)
  const existing = await getVolunteerSignup(orgId, eventId, volunteerId);
  if (existing) throw new Error('Already signed up as a volunteer for this event.');

  // Atomically create the signup doc and increment the event counter
  const batch = writeBatch(db);
  const signupRef = doc(signupsCol());  // auto-generated ID
  batch.set(signupRef, {
    orgId,
    event_id:       eventId,
    volunteer_id:   volunteerId,
    volunteer_name: volunteerName,
    signed_up_at:   now(),
  });
  batch.update(eventDoc(eventId), { volunteer_signup_count: increment(1), updated_at: now() });
  await batch.commit();
};

/** Volunteer withdraws their sign-up */
export const withdrawVolunteerSignup = async (orgId, eventId, volunteerId) => {
  const existing = await getVolunteerSignup(orgId, eventId, volunteerId);
  if (!existing) throw new Error('No volunteer signup found to withdraw.');

  const batch = writeBatch(db);
  batch.delete(signupDocRef(existing.id));
  batch.update(eventDoc(eventId), { volunteer_signup_count: increment(-1), updated_at: now() });
  await batch.commit();
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 2 — REGISTRATION & WAITLIST
// ═══════════════════════════════════════════════════════════════════════════════

export const getRegistrations = async (orgId, eventId, statusFilter = null) => {
  // Fetch all registrations for this event, sort client-side to avoid extra indexes
  const all = toArr(await getDocs(
    query(regsCol(), where('orgId', '==', orgId), where('event_id', '==', eventId))
  ));
  const filtered = statusFilter ? all.filter(r => r.status === statusFilter) : all;
  return filtered.sort((a, b) => {
    const ta = a.registered_at?.toDate ? a.registered_at.toDate() : new Date(a.registered_at || 0);
    const tb = b.registered_at?.toDate ? b.registered_at.toDate() : new Date(b.registered_at || 0);
    return ta - tb;
  });
};

export const getMemberRegistration = async (orgId, eventId, memberId) => {
  const q = query(
    regsCol(),
    where('orgId', '==', orgId),
    where('event_id', '==', eventId),
    where('member_id', '==', memberId)
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const getMemberAllRegistrations = async (orgId, memberId) => {
  const snap = await getDocs(
    query(
      regsCol(),
      where('orgId', '==', orgId),
      where('member_id', '==', memberId),
      orderBy('registered_at', 'desc')
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getRegistrationCount = async (orgId, eventId) => {
  const all = toArr(await getDocs(
    query(regsCol(), where('orgId', '==', orgId), where('event_id', '==', eventId))
  ));
  const confirmed  = all.filter(r => r.status === 'CONFIRMED').length;
  const waitlisted = all.filter(r => r.status === 'WAITLISTED').length;
  return { confirmed, waitlisted };
};

/**
 * Count confirmed/waitlisted registrations split by registrant_type.
 * registrant_type 'member'    → member slot
 * registrant_type 'volunteer' → volunteer attendee slot
 * Docs without registrant_type are treated as 'member' (legacy).
 */
export const getRegistrationCountByType = async (orgId, eventId) => {
  const all = toArr(await getDocs(
    query(regsCol(), where('orgId', '==', orgId), where('event_id', '==', eventId))
  ));
  const isVol = r => r.registrant_type === 'volunteer';
  return {
    member_confirmed:    all.filter(r => !isVol(r) && (r.status === 'CONFIRMED' || r.status === 'CHECKED_IN')).length,
    member_waitlisted:   all.filter(r => !isVol(r) &&  r.status === 'WAITLISTED').length,
    volunteer_confirmed: all.filter(r =>  isVol(r) && (r.status === 'CONFIRMED' || r.status === 'CHECKED_IN')).length,
    volunteer_waitlisted:all.filter(r =>  isVol(r) &&  r.status === 'WAITLISTED').length,
  };
};

/** Get a single volunteer's event-attendance registration (NOT the volunteer_signups role sign-up) */
export const getVolunteerEventRegistration = async (orgId, eventId, volunteerId) => {
  const q = query(
    regsCol(),
    where('orgId', '==', orgId),
    where('event_id', '==', eventId),
    where('member_id', '==', volunteerId),
    where('registrant_type', '==', 'volunteer')
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};

/** Get all event-attendance registrations for a volunteer (root-level collection) */
export const getVolunteerAllEventRegistrations = async (orgId, volunteerId) => {
  const snap = await getDocs(
    query(
      regsCol(),
      where('orgId', '==', orgId),
      where('member_id', '==', volunteerId),
      orderBy('registered_at', 'desc')
    )
  );
  // Filter volunteer type client-side to avoid extra composite index
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => r.registrant_type === 'volunteer');
};

/**
 * Register a member OR a volunteer-as-attendee for an event.
 *
 * @param registrantType  'member' (default) | 'volunteer'
 *   - 'member'    uses event.member_capacity
 *   - 'volunteer' uses event.volunteer_capacity
 *
 * Waitlists are per-type: members queue for member slots, volunteers for volunteer slots.
 */
export const registerForEvent = async (
  orgId, eventId, memberId, memberName, memberEmail,
  registeredBy, photoConsent = null, registrantType = 'member'
) => {
  const event = await getEvent(orgId, eventId);
  if (!event) throw new Error('Event not found');

  // Resolve capacity for this registrant type
  const capacity = registrantType === 'volunteer'
    ? (event.volunteer_capacity != null ? event.volunteer_capacity : 0)
    : (event.member_capacity    != null ? event.member_capacity    : 0);

  if (capacity === 0 && registrantType === 'volunteer') {
    throw new Error('This event does not have volunteer attendance slots.');
  }

  // Check existing registration of the same type
  const existingQ = query(
    regsCol(),
    where('orgId', '==', orgId),
    where('event_id', '==', eventId),
    where('member_id', '==', memberId),
    where('registrant_type', '==', registrantType)
  );
  const existingSnap = await getDocs(existingQ);
  const existing = existingSnap.empty ? null : existingSnap.docs[0].data();
  if (existing && existing.status !== 'CANCELLED') throw new Error('Already registered for this event');

  // Count confirmed of this type
  const counts = await getRegistrationCountByType(orgId, eventId);
  const confirmedCount = registrantType === 'volunteer'
    ? counts.volunteer_confirmed
    : counts.member_confirmed;
  const waitlistedCount = registrantType === 'volunteer'
    ? counts.volunteer_waitlisted
    : counts.member_waitlisted;

  const isConfirmed    = confirmedCount < capacity;
  const waitlistPosition = isConfirmed ? null : waitlistedCount + 1;

  const token = generateQRToken(orgId, eventId, memberId);
  const regRef = await addDoc(regsCol(), {
    member_id:           memberId,
    member_name:         memberName,
    member_email:        memberEmail,
    registrant_type:     registrantType,
    orgId,
    event_id:            eventId,
    status:              isConfirmed ? 'CONFIRMED' : 'WAITLISTED',
    waitlist_position:   waitlistPosition,
    registered_at:       now(),
    registered_by:       registeredBy,
    checked_in_at:       null,
    qr_code_token:       token,
    cancelled_at:        null,
    cancellation_reason: null,
    photo_consent:       photoConsent
  });

  return { id: regRef.id, status: isConfirmed ? 'CONFIRMED' : 'WAITLISTED', waitlist_position: waitlistPosition, qr_code_token: token };
};

/**
 * Cancel a registration and automatically promote the first waitlisted person of the SAME type.
 * Sends an in-portal notification to the promoted person.
 */
export const cancelRegistration = async (orgId, eventId, registrationId, reason, cancelledBy) => {
  let promotedRecipientId = null;
  let promotedRecipientName = '';
  const event = await getEvent(orgId, eventId);

  await runTransaction(db, async (tx) => {
    const theRegDoc = regDoc(registrationId);
    const regSnap = await tx.get(theRegDoc);
    if (!regSnap.exists()) throw new Error('Registration not found');

    const reg = regSnap.data();
    // Mark this registration cancelled
    tx.update(theRegDoc, {
      status: 'CANCELLED',
      cancelled_at: now(),
      cancellation_reason: reason,
      cancelled_by: cancelledBy,
      updated_at: now()
    });

    // If this was a CONFIRMED registration, promote waitlist #1 of the same type
    if (reg.status === 'CONFIRMED') {
      const regType = reg.registrant_type || 'member';
      // Build query: same org + event + WAITLISTED, ordered by position
      // (queries inside runTransaction are allowed but not transactional;
      //  this is acceptable for waitlist promotion which is best-effort)
      let waitlistQuery;
      if (regType === 'volunteer') {
        waitlistQuery = query(
          regsCol(),
          where('orgId', '==', orgId),
          where('event_id', '==', eventId),
          where('status', '==', 'WAITLISTED'),
          where('registrant_type', '==', 'volunteer'),
          orderBy('waitlist_position', 'asc'),
          limit(10)
        );
      } else {
        waitlistQuery = query(
          regsCol(),
          where('orgId', '==', orgId),
          where('event_id', '==', eventId),
          where('status', '==', 'WAITLISTED'),
          orderBy('waitlist_position', 'asc'),
          limit(20)
        );
      }

      const waitlistSnap = await getDocs(waitlistQuery);
      const filteredDocs = regType === 'volunteer'
        ? waitlistSnap.docs
        : waitlistSnap.docs.filter(d => (d.data().registrant_type || 'member') === 'member');

      if (filteredDocs.length > 0) {
        const firstWaiting = filteredDocs[0];
        const firstData = firstWaiting.data();
        tx.update(firstWaiting.ref, {
          status: 'CONFIRMED',
          waitlist_position: null,
          promoted_at: now(),
          updated_at: now()
        });
        promotedRecipientId   = firstData.member_id;
        promotedRecipientName = firstData.member_name;

        // Shift remaining waitlist positions down by 1
        filteredDocs.slice(1).forEach((d, i) => {
          tx.update(d.ref, { waitlist_position: i + 1, updated_at: now() });
        });
      }
    }
  });

  // Send in-portal notification to the promoted person (outside transaction)
  if (promotedRecipientId) {
    try {
      const { createNotification, NOTIFICATION_TYPES } = await import('./notificationsApi');
      await createNotification(
        orgId,
        promotedRecipientId,
        NOTIFICATION_TYPES.WAITLIST_PROMOTED,
        'You\'ve been confirmed!',
        `Great news! A spot opened up for "${event?.title || 'an event'}" and you've been moved from the waitlist to confirmed. See you there!`,
        { event_id: eventId }
      );
    } catch (_) { /* non-critical — don't fail the cancellation */ }
  }
};

export const manualAddRegistrant = async (orgId, eventId, memberId, memberName, memberEmail, adminId, note = '') => {
  return registerForEvent(orgId, eventId, memberId, memberName, memberEmail, adminId);
};

export const promoteFromWaitlist = async (orgId, eventId, registrationId, adminId, reason) => {
  await runTransaction(db, async (tx) => {
    const theRegDoc = regDoc(registrationId);
    const snap = await tx.get(theRegDoc);
    if (!snap.exists()) throw new Error('Registration not found');

    tx.update(theRegDoc, {
      status: 'CONFIRMED',
      waitlist_position: null,
      manually_promoted: true,
      promoted_by: adminId,
      promotion_reason: reason,
      promoted_at: now(),
      updated_at: now()
    });
  });
};

export const overrideCapacity = async (orgId, eventId, newMemberCapacity, adminId, reason) => {
  await updateDoc(eventDoc(eventId), {
    member_capacity: newMemberCapacity,
    capacity_override_reason: reason,
    capacity_override_by: adminId,
    capacity_override_at: now(),
    updated_at: now()
  });
};

/**
 * Estimate the audience size for a new event by filtering the org's members
 * against optional criteria: districts, interests, employment statuses.
 */
export const estimateAudience = async (orgId, filters = {}) => {
  const { districts = [], interests = [], employmentStatuses = [] } = filters;
  const snap = await getDocs(
    query(collection(db, 'members'), where('orgId', '==', orgId))
  );
  const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const filtered = all.filter(m => {
    if (districts.length && !districts.includes(m.district)) return false;
    if (interests.length && !interests.some(i => (m.interests || []).includes(i))) return false;
    if (employmentStatuses.length && !employmentStatuses.includes(m.employmentStatus)) return false;
    return true;
  });
  return { total: all.length, filtered: filtered.length };
};

export const exportRegistrants = (registrations) => {
  const headers = ['Name', 'Email', 'Status', 'Waitlist Position', 'Registered At', 'Photo Consent'];
  const rows = registrations
    .filter(r => r.status === 'CONFIRMED')
    .map(r => [
      r.member_name,
      r.member_email,
      r.status,
      r.waitlist_position || '',
      fmtDate(r.registered_at),
      r.photo_consent === true ? 'Yes' : r.photo_consent === false ? 'No' : 'Not asked'
    ]);
  return [headers, ...rows].map(row => row.join(',')).join('\n');
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 3 — ATTENDANCE & CHECK-IN
// ═══════════════════════════════════════════════════════════════════════════════

export const checkInByQR = async (orgId, eventId, qrToken) => {
  const q = query(
    regsCol(),
    where('orgId', '==', orgId),
    where('event_id', '==', eventId),
    where('qr_code_token', '==', qrToken)
  );
  const snap = await getDocs(q);

  if (snap.empty) return { success: false, error: 'QR code not recognised' };
  const theRegDoc = snap.docs[0];
  const reg = theRegDoc.data();

  if (reg.status === 'WAITLISTED') return { success: false, error: `${reg.member_name} is on the waitlist — not confirmed`, member_name: reg.member_name };
  if (reg.status === 'CANCELLED')  return { success: false, error: `${reg.member_name}'s registration was cancelled`, member_name: reg.member_name };
  if (reg.status === 'CHECKED_IN') return { success: false, error: `${reg.member_name} is already checked in`, member_name: reg.member_name, already: true };

  await updateDoc(theRegDoc.ref, {
    status: 'CHECKED_IN', checked_in_at: now(), updated_at: now()
  });

  return { success: true, member_name: reg.member_name, member_id: reg.member_id };
};

export const checkInBySearch = async (orgId, eventId, searchTerm) => {
  const allRegs = await getRegistrations(orgId, eventId);
  const term = searchTerm.toLowerCase();
  return allRegs.filter(r =>
    r.member_name?.toLowerCase().includes(term) ||
    r.member_id?.toLowerCase().includes(term) ||
    r.member_email?.toLowerCase().includes(term)
  );
};

export const manualCheckIn = async (orgId, eventId, registrationId, adminId, reason) => {
  await updateDoc(regDoc(registrationId), {
    status: 'CHECKED_IN',
    checked_in_at: now(),
    manual_checkin: true,
    manual_checkin_by: adminId,
    manual_checkin_reason: reason,
    updated_at: now()
  });
};

export const markNoShows = async (orgId, eventId, adminId) => {
  const confirmed = await getRegistrations(orgId, eventId, 'CONFIRMED');
  const batch = writeBatch(db);
  const noShowIds = [];

  confirmed.forEach(reg => {
    batch.update(regDoc(reg.id), { status: 'NO_SHOW', no_show_marked_at: now(), no_show_marked_by: adminId });
    noShowIds.push(reg.member_id);
  });

  await batch.commit();

  // Update member no-show history (stays in org subcollection)
  for (const memberId of noShowIds) {
    const histRef = orgDoc(orgId, 'memberEventHistory', memberId);
    const histSnap = await getDoc(histRef);
    const hist = histSnap.exists() ? histSnap.data() : { confirmed_events: [], attended_events: [], no_show_events: [], no_show_count: 0 };
    await setDoc(histRef, {
      ...hist,
      no_show_events: [...(hist.no_show_events || []), eventId],
      no_show_count: (hist.no_show_count || 0) + 1
    }, { merge: true });
  }

  return noShowIds.length;
};

/**
 * Look up a registration by QR token and optionally record attendance.
 *
 * dryRun = true  → validate only, return member/event info without writing
 * dryRun = false → validate + update registration to CHECKED_IN + write to eventsAttendance
 */
export const recordAttendanceByQR = async (orgId, qrToken, adminId, dryRun = false) => {
  const q = query(regsCol(), where('orgId', '==', orgId), where('qr_code_token', '==', qrToken));
  const snap = await getDocs(q);

  if (snap.empty) return { success: false, error: 'QR code not recognised for this organisation.' };

  const regSnap = snap.docs[0];
  const reg     = regSnap.data();

  if (reg.status === 'WAITLISTED')
    return { success: false, error: `${reg.member_name} is on the waitlist and is not confirmed.`, member_name: reg.member_name };
  if (reg.status === 'CANCELLED')
    return { success: false, error: `${reg.member_name}'s registration has been cancelled.`, member_name: reg.member_name };
  if (reg.status === 'CHECKED_IN')
    return { success: false, error: `${reg.member_name} is already checked in.`, member_name: reg.member_name };

  const eventSnap  = await getDoc(eventDoc(reg.event_id));
  const eventData  = eventSnap.exists() ? eventSnap.data() : null;

  if (dryRun) {
    return {
      success:        true,
      qr_token:       qrToken,
      registration_id: regSnap.id,
      member_id:      reg.member_id,
      member_name:    reg.member_name,
      member_email:   reg.member_email || '',
      registrant_type: reg.registrant_type || 'member',
      current_status: reg.status,
      event_id:       reg.event_id,
      event_title:    eventData?.title || '',
      event_datetime: eventData?.start_datetime || null,
    };
  }

  const batch = writeBatch(db);

  batch.update(regSnap.ref, { status: 'CHECKED_IN', checked_in_at: now(), updated_at: now() });

  const attendanceRef = doc(collection(db, 'eventsAttendance'));
  batch.set(attendanceRef, {
    orgId,
    eventId:         reg.event_id,
    eventTitle:      eventData?.title || '',
    registrationId:  regSnap.id,
    memberId:        reg.member_id,
    memberName:      reg.member_name,
    memberEmail:     reg.member_email || '',
    registrantType:  reg.registrant_type || 'member',
    scannedAt:       serverTimestamp(),
    scannedBy:       adminId,
  });

  await batch.commit();

  return {
    success:        true,
    qr_token:       qrToken,
    registration_id: regSnap.id,
    member_id:      reg.member_id,
    member_name:    reg.member_name,
    member_email:   reg.member_email || '',
    registrant_type: reg.registrant_type || 'member',
    event_id:       reg.event_id,
    event_title:    eventData?.title || '',
    event_datetime: eventData?.start_datetime || null,
  };
};

export const getAttendanceSummary = async (orgId, eventId) => {
  const all = await getRegistrations(orgId, eventId);
  return {
    confirmed:  all.filter(r => r.status === 'CONFIRMED').length,
    checked_in: all.filter(r => r.status === 'CHECKED_IN').length,
    waitlisted: all.filter(r => r.status === 'WAITLISTED').length,
    no_show:    all.filter(r => r.status === 'NO_SHOW').length,
    cancelled:  all.filter(r => r.status === 'CANCELLED').length,
    total:      all.length
  };
};

export const getMemberEventHistory = async (orgId, memberId) => {
  const ref = orgDoc(orgId, 'memberEventHistory', memberId);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : { confirmed_events: [], attended_events: [], no_show_events: [], no_show_count: 0 };
};

export const getNoShowThresholdBreaches = async (orgId, threshold = 3) => {
  const q = query(orgCol(orgId, 'memberEventHistory'), where('no_show_count', '>=', threshold));
  return toArr(await getDocs(q));
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 4 — EVENT CALENDAR & DISCOVERY
// ═══════════════════════════════════════════════════════════════════════════════

export const getUpcomingEvents = async (orgId, days = 90) => {
  // Fetch all PUBLISHED events for this org and apply the date window client-side.
  // This handles the case where start_datetime may be stored as a Timestamp (new
  // events) or a legacy string — Firestore's server-side range comparison against
  // a Timestamp cutoff silently excludes string-typed docs, so we filter here.
  const nowMs    = Date.now();
  const cutoffMs = nowMs + days * 86400000;
  const q = query(
    eventsCol(),
    where('orgId', '==', orgId),
    where('status', '==', 'PUBLISHED')
  );
  const events = sortByStart(toArr(await getDocs(q)));
  return events.filter(e => {
    if (!e.start_datetime) return true;
    const d = e.start_datetime.toDate ? e.start_datetime.toDate() : new Date(e.start_datetime);
    if (isNaN(d.getTime())) return true;
    return d.getTime() >= nowMs && d.getTime() <= cutoffMs;
  });
};

export const searchEvents = async (orgId, keyword) => {
  const all = await getPublishedEvents(orgId);
  const kw = keyword.toLowerCase();
  return all.filter(e =>
    e.title?.toLowerCase().includes(kw) ||
    e.description?.toLowerCase().includes(kw) ||
    e.tags?.some(t => t.toLowerCase().includes(kw))
  );
};

export const getEventsByCategory = async (orgId, category) => {
  const all = await getPublishedEvents(orgId);
  return all.filter(e => e.category === category);
};

export const generateICS = (event) => {
  const start = event.start_datetime?.toDate ? event.start_datetime.toDate() : new Date(event.start_datetime);
  const end   = event.end_datetime?.toDate   ? event.end_datetime.toDate()   : new Date(event.end_datetime);

  const fmt = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//BespokeBridge//Events//EN',
    'BEGIN:VEVENT',
    `UID:${event.id}@bespokebridge`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}`,
    `LOCATION:${event.location?.name || ''} ${event.location?.address || ''}`,
    'END:VEVENT', 'END:VCALENDAR'
  ].join('\r\n');
};

// ═══════════════════════════════════════════════════════════════════════════════
// TOOL 5 — EVENT CONTENT & MEDIA
// ═══════════════════════════════════════════════════════════════════════════════

export const getEventMedia = async (orgId, eventId, typeFilter = null) => {
  const all = toArr(await getDocs(
    query(mediaCol(), where('orgId', '==', orgId), where('event_id', '==', eventId))
  ));
  const filtered = typeFilter
    ? all.filter(m => m.type === typeFilter && m.moderation_status === 'APPROVED')
    : all.filter(m => m.moderation_status === 'APPROVED');
  return filtered.sort((a, b) => {
    const ta = a.uploaded_at?.toDate ? a.uploaded_at.toDate() : new Date(a.uploaded_at || 0);
    const tb = b.uploaded_at?.toDate ? b.uploaded_at.toDate() : new Date(b.uploaded_at || 0);
    return tb - ta;  // newest first
  });
};

export const getPendingMediaModeration = async (orgId, eventId) => {
  const all = toArr(await getDocs(
    query(mediaCol(), where('orgId', '==', orgId), where('event_id', '==', eventId))
  ));
  return all.filter(m => m.moderation_status === 'PENDING');
};

export const uploadEventMaterial = async (orgId, eventId, file, type, visibility, uploadedBy, uploadedByName, versionNote = '') => {
  const path = `events/${orgId}/${eventId}/media/${Date.now()}_${file.name}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const fileUrl = await getDownloadURL(storageRef);

  const docRef = await addDoc(mediaCol(), {
    orgId,
    event_id: eventId,
    type,
    file_url: fileUrl,
    file_name: file.name,
    storage_path: path,
    visibility,
    moderation_status: 'APPROVED',  // admin uploads auto-approved
    uploaded_by: uploadedBy,
    uploaded_by_name: uploadedByName,
    version_note: versionNote,
    uploaded_at: now()
  });
  return docRef.id;
};

export const submitMemberPhoto = async (orgId, eventId, file, memberId, memberName) => {
  const path = `events/${orgId}/${eventId}/media/member_${memberId}_${Date.now()}_${file.name}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const fileUrl = await getDownloadURL(storageRef);

  const docRef = await addDoc(mediaCol(), {
    orgId,
    event_id: eventId,
    type: 'POST_EVENT_PHOTO',
    file_url: fileUrl,
    file_name: file.name,
    storage_path: path,
    visibility: 'ALL_MEMBERS',
    moderation_status: 'PENDING',   // member uploads need moderation
    uploaded_by: memberId,
    uploaded_by_name: memberName,
    member_submitted: true,
    uploaded_at: now()
  });
  return docRef.id;
};

export const moderateMedia = async (orgId, eventId, mediaId, approved, adminId, note = '') => {
  await updateDoc(mediaDocRef(mediaId), {
    moderation_status: approved ? 'APPROVED' : 'REJECTED',
    moderated_by: adminId,
    moderation_note: note,
    moderated_at: now()
  });
};

export const updateMediaPrivacy = async (orgId, eventId, mediaId, visibility) => {
  await updateDoc(mediaDocRef(mediaId), { visibility, updated_at: now() });
};

/**
 * Send a media item to all registrants (members + volunteers) who signed up for the event.
 * Sends regardless of current membership status — anyone who registered is included.
 * Statuses included: CONFIRMED, CHECKED_IN, WAITLISTED, NO_SHOW (excludes CANCELLED).
 * Returns { sent: number } — count of notifications dispatched.
 */
export const sendMaterialToRegistrants = async (orgId, eventId, mediaId, adminId, adminName) => {
  const [mediaSnap, regs] = await Promise.all([
    getDoc(mediaDocRef(mediaId)),
    getRegistrations(orgId, eventId),
  ]);

  if (!mediaSnap.exists()) throw new Error('Media not found.');
  const media = mediaSnap.data();

  const eventSnap = await getDoc(eventDoc(eventId));
  const eventTitle = eventSnap.exists() ? eventSnap.data().title : 'an event';

  // Deduplicate by member_id; exclude cancelled registrations
  const recipients = new Map();
  for (const reg of regs) {
    if (reg.status === 'CANCELLED') continue;
    if (!recipients.has(reg.member_id)) {
      recipients.set(reg.member_id, reg.registrant_type || 'member');
    }
  }

  const { createNotification, NOTIFICATION_TYPES } = await import('./notificationsApi');

  const dispatches = [];
  for (const [recipientId] of recipients) {
    dispatches.push(
      createNotification(
        orgId,
        recipientId,
        NOTIFICATION_TYPES.EVENT_MATERIAL_SHARED,
        `New material shared: "${media.file_name}"`,
        `${adminName || 'Admin'} has shared a file for "${eventTitle}". Tap to view: ${media.file_url}`,
        { event_id: eventId, media_id: mediaId, file_url: media.file_url, file_name: media.file_name }
      )
    );
  }
  await Promise.all(dispatches);

  // Record dispatch log on the media document
  await updateDoc(mediaDocRef(mediaId), {
    last_dispatched_at: now(),
    last_dispatched_by: adminId,
    last_dispatched_by_name: adminName || '',
    dispatch_count: (media.dispatch_count || 0) + 1,
  });

  return { sent: recipients.size };
};

export const deleteMedia = async (orgId, eventId, mediaId) => {
  const snap = await getDoc(mediaDocRef(mediaId));
  if (snap.exists() && snap.data().storage_path) {
    try { await deleteObject(ref(storage, snap.data().storage_path)); } catch (_) {}
  }
  await deleteDoc(mediaDocRef(mediaId));
};
